<template>
  <div class="content" style="padding: 16px 0">
    <!-- Alertes + Export -->
    <div v-if="overview" style="display: flex; align-items: start; gap: 8px; margin-bottom: 8px">
      <v-card v-if="overview.alertes && overview.alertes.total > 0"
        outlined class="alert-card" :ripple="false"
        style="cursor: pointer; flex: 1"
        @click="showAlertes = !showAlertes">
        <v-card-text style="padding: 10px 16px">
          <div style="display: flex; align-items: center; justify-content: space-between">
            <div style="display: flex; align-items: center; gap: 8px">
              <v-icon small :color="overview.alertes.critical > 0 ? '#f44336' : '#ff9800'">
                {{ overview.alertes.critical > 0 ? 'mdi-alert-circle' : 'mdi-alert' }}
              </v-icon>
              <span style="font-size: 13px">
                <strong>{{ overview.alertes.total }}</strong> alerte{{ overview.alertes.total > 1 ? 's' : '' }}
                <span v-if="overview.alertes.critical > 0" style="color: #f44336; font-size: 12px">
                  ({{ overview.alertes.critical }} critique{{ overview.alertes.critical > 1 ? 's' : '' }})
                </span>
              </span>
            </div>
            <v-icon small color="#999">{{ showAlertes ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <div v-if="showAlertes" style="margin-top: 8px; border-top: 1px solid #eee; padding-top: 8px">
            <div v-for="(a, i) in overview.alertes.items" :key="i"
              style="padding: 3px 0; font-size: 12px; display: flex; align-items: center; gap: 8px">
              <v-icon x-small :color="a.severity === 'critical' ? '#f44336' : a.severity === 'warning' ? '#ff9800' : '#2196f3'">
                {{ a.severity === 'critical' ? 'mdi-alert-circle' : a.severity === 'warning' ? 'mdi-alert' : 'mdi-information' }}
              </v-icon>
              <span>{{ a.message }}</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
      <div style="display: flex; gap: 6px; flex-shrink: 0; margin-left: auto">
        <v-btn outlined color="#e2007a" :loading="exporting"
          class="export-btn"
          @click.stop="exportXlsx">
          <v-icon small left>mdi-file-excel-outline</v-icon>
          XLSX
        </v-btn>
        <v-btn outlined color="#e2007a" :loading="exportingPdf"
          class="export-btn"
          @click.stop="exportPdf">
          <v-icon small left>mdi-file-pdf-box</v-icon>
          PDF
        </v-btn>
      </div>
    </div>

    <!-- KPI Cards -->
    <v-row v-if="kpis" dense>
      <v-col cols="12" sm="6" md="3" v-for="kpi in kpiCards" :key="kpi.label" class="d-flex">
        <v-card outlined style="border-top: 3px solid; width: 100%" :style="{ borderTopColor: kpi.color }">
          <v-card-text style="padding: 16px; min-height: 90px; display: flex; align-items: center">
            <div style="display: flex; align-items: center; gap: 12px">
              <v-icon :color="kpi.color" large>{{ kpi.icon }}</v-icon>
              <div>
                <div style="font-size: 28px; font-weight: bold; line-height: 1">{{ kpi.value }}</div>
                <div style="font-size: 12px; color: #888; margin-top: 2px">{{ kpi.label }}</div>
                <div v-if="kpi.sub" :style="{ fontSize: '11px', color: kpi.subColor || '#888', marginTop: '2px' }">
                  {{ kpi.sub }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Objectif commercial : banniere fine pleine largeur -->
    <div v-if="overview && overview.commercialGoal" style="margin-top: 12px">
      <GaugeSegurCA
        :ca-engage="overview.commercialGoal.engagedHT"
        :objectif="overview.commercialGoal.objectiveHT"
      />
    </div>

    <!-- Graphiques : évolution + répartition -->
    <v-row v-if="overview" dense style="margin-top: 16px">
      <v-col cols="12" md="8" class="d-flex">
        <v-card outlined style="width: 100%">
          <v-card-title style="font-size: 14px; padding: 12px 16px">Évolution mensuelle des BDC V2</v-card-title>
          <v-card-text>
            <apexchart type="area" height="300" :options="evolutionOptions" :series="evolutionSeries"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" class="d-flex">
        <v-card outlined style="width: 100%">
          <v-card-title style="font-size: 14px; padding: 12px 16px">Répartition par état</v-card-title>
          <v-card-text style="display: flex; align-items: center; justify-content: center; flex: 1">
            <apexchart type="donut" height="260" :options="donutOptions" :series="donutSeries"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Mix V2/V1V12 + Rejets ASP -->
    <v-row v-if="overview && overview.mixParMois && overview.mixParMois.length > 0" dense style="margin-top: 16px">
      <v-col cols="12" md="7" class="d-flex">
        <v-card outlined style="width: 100%">
          <v-card-title style="font-size: 14px; padding: 12px 16px">
            Répartition V2 / V1V12 par mois
            <span v-if="panierMoyen" style="font-size: 12px; color: #888; margin-left: 12px">Panier moyen : {{ panierMoyen }} € HT</span>
          </v-card-title>
          <v-card-text>
            <apexchart type="bar" height="280" :options="mixOptions" :series="mixSeries"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="5" class="d-flex">
        <v-card outlined style="width: 100%">
          <v-card-title style="font-size: 14px; padding: 12px 16px">
            Rejets ASP
            <v-chip small class="ml-2" :color="rejetsColor" text-color="white">
              {{ overview.rejetsAsp ? overview.rejetsAsp.total : 0 }}
            </v-chip>
          </v-card-title>
          <v-card-text style="display: flex; align-items: center; justify-content: center; flex-direction: column; flex: 1">
            <template v-if="overview.rejetsAsp && overview.rejetsAsp.total > 0">
              <div style="font-size: 13px; color: #888; margin-bottom: 8px">
                Taux de rejet : <strong :style="{ color: rejetsColor }">{{ overview.rejetsAsp.tauxRejet }}%</strong>
              </div>
              <apexchart type="donut" height="220" :options="rejetsDonutOptions" :series="rejetsDonutSeries"></apexchart>
            </template>
            <div v-else style="text-align: center; padding: 40px 16px; color: #4caf50; font-size: 13px">
              <v-icon color="green" style="margin-bottom: 8px">mdi-check-circle-outline</v-icon>
              <div>Aucun rejet ASP</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Financier : parcours de l'argent -->
    <v-row v-if="kpis" dense style="margin-top: 16px">
      <v-col cols="12" sm="6" md="3" v-for="fin in financialCardsV2" :key="fin.label">
        <v-card outlined>
          <v-card-text style="text-align: center; padding: 16px">
            <div style="font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px">{{ fin.label }}</div>
            <div style="font-size: 22px; font-weight: bold; margin-top: 4px">{{ formatMoney(fin.ht) }} € HT</div>
            <div style="font-size: 13px; color: green">{{ formatMoney(fin.ttc) }} € TTC</div>
            <div v-if="fin.sub" style="font-size: 10px; color: #aaa; margin-top: 2px">{{ fin.sub }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- PS à risque -->
    <v-row v-if="overview" dense style="margin-top: 16px">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title style="font-size: 14px; padding: 12px 16px">
            Médecins à risque (Prestation Secondaire)
            <v-chip small class="ml-2" :color="psAtRiskDisplay.length > 0 ? 'red' : 'green'" text-color="white">{{ psAtRiskDisplay.length }}</v-chip>
          </v-card-title>
          <v-card-text style="padding: 0 16px 16px">
            <v-simple-table v-if="psAtRiskDisplay.length > 0" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Médecin</th>
                    <th>RPPS</th>
                    <th>Dm/Dt</th>
                    <th>Échéance</th>
                    <th>Jours restants</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(ps, i) in psAtRiskDisplay" :key="i">
                    <td>{{ ps.nom }} {{ ps.prenom }}</td>
                    <td style="font-family: monospace; font-size: 12px">{{ ps.RPPS }}</td>
                    <td>
                      <span :style="{ color: ps.dmDtRatio < 40 ? '#f44336' : '#ff9800', fontWeight: 'bold' }">
                        {{ ps.dmDtRatio }}%
                      </span>
                      <span style="font-size: 11px; color: #888"> ({{ ps.dm }}/{{ ps.dt }})</span>
                    </td>
                    <td>{{ formatDate(ps.dateFin) }}</td>
                    <td>
                      <v-chip x-small :color="ps.joursRestants < 30 ? 'red' : ps.joursRestants < 60 ? 'orange' : 'grey'" text-color="white">
                        {{ ps.joursRestants }}j
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div v-else style="text-align: center; padding: 16px; color: #4caf50; font-size: 13px">
              Aucun médecin à risque actuellement
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- BDC en attente d'action -->
    <v-row v-if="overview" dense style="margin-top: 16px">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title style="font-size: 14px; padding: 12px 16px">
            Bons de commande en attente d'action
            <v-chip small class="ml-2" :color="bdcZombiesDisplay.length > 0 ? 'orange' : 'green'" text-color="white">{{ bdcZombiesDisplay.length }}</v-chip>
          </v-card-title>
          <v-card-text style="padding: 0 16px 16px">
            <v-simple-table v-if="bdcZombiesDisplay.length > 0" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Médecin</th>
                    <th>RPPS</th>
                    <th>Étape</th>
                    <th>Âge</th>
                    <th>Créé le</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(z, i) in bdcZombiesDisplay" :key="i">
                    <td>{{ z.nom }} {{ z.prenom }}</td>
                    <td style="font-family: monospace; font-size: 12px">{{ z.RPPS }}</td>
                    <td>
                      <v-chip x-small :color="z.etape === 'Non signé' ? 'red' : 'orange'" text-color="white">{{ z.etape }}</v-chip>
                    </td>
                    <td>
                      <v-chip x-small :color="z.ageJours > 60 ? 'red' : 'orange'" text-color="white">{{ z.ageJours }}j</v-chip>
                    </td>
                    <td>{{ formatDate(z.createdAt) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div v-else style="text-align: center; padding: 16px; color: #4caf50; font-size: 13px">
              Aucun BDC en attente d'action
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Parcours des bons de commande -->
    <v-row v-if="overview && overview.funnel" dense style="margin-top: 16px">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title style="font-size: 14px; padding: 12px 16px">Parcours des bons de commande</v-card-title>
          <v-card-text style="padding: 8px 16px 16px">
            <div style="display: flex; align-items: stretch; gap: 0; width: 100%">
              <template v-for="(step, i) in overview.funnel.steps">
                <div :key="'arrow-' + i" v-if="i > 0" style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 0 2px; min-width: 60px">
                  <v-icon small color="#ccc">mdi-arrow-right</v-icon>
                  <div v-if="step.avgDays != null" style="font-size: 11px; color: #e2007a; font-weight: bold; margin-top: 2px">
                    {{ formatAvgDays(step.avgDays) }}
                  </div>
                  <div v-else style="font-size: 11px; color: #ccc; margin-top: 2px">-</div>
                  <div style="font-size: 9px; color: #aaa">moy.</div>
                </div>
                <div :key="'step-' + i" style="flex: 1; text-align: center; padding: 12px 8px; border-radius: 8px; position: relative"
                     :style="{ background: stepBackground(i, overview.funnel.steps.length) }">
                  <div style="font-size: 24px; font-weight: bold; color: white; line-height: 1">{{ step.count }}</div>
                  <div style="font-size: 11px; color: rgba(255,255,255,0.85); margin-top: 4px">{{ step.label }}</div>
                  <div v-if="i > 0 && overview.funnel.steps[0].count > 0" style="font-size: 10px; color: rgba(255,255,255,0.7); margin-top: 2px">
                    {{ Math.round(step.count * 100 / overview.funnel.steps[0].count) }}%
                  </div>
                </div>
              </template>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- CA mensuel bar chart -->
    <v-row v-if="overview && overview.caParMois && overview.caParMois.length > 0" dense style="margin-top: 16px">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title style="font-size: 14px; padding: 12px 16px">CA mensuel (€ HT)</v-card-title>
          <v-card-text>
            <apexchart type="bar" height="300" :options="caOptions" :series="caSeries"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Courbe cumulative V2 vs V1 (Prokov uniquement) -->
    <v-row v-if="overview && overview.evolutionV1.length > 0" dense style="margin-top: 16px">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title style="font-size: 14px; padding: 12px 16px">Comparaison V1 vs V2 (BDC cumulés)</v-card-title>
          <v-card-text>
            <apexchart type="line" height="300" :options="comparisonOptions" :series="comparisonSeries"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Géo + Benchmark -->
    <v-row v-if="overview" dense style="margin-top: 16px">
      <v-col cols="12" :md="hasBenchmark ? 7 : 12" class="d-flex" v-if="overview.topDepartements && overview.topDepartements.length > 0">
        <v-card outlined style="width: 100%">
          <v-card-title style="font-size: 14px; padding: 12px 16px">Top départements</v-card-title>
          <v-card-text>
            <apexchart type="bar" :height="Math.max(200, overview.topDepartements.length * 32)" :options="deptOptions" :series="deptSeries"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="5" class="d-flex" v-if="hasBenchmark">
        <v-card outlined style="width: 100%">
          <v-card-title style="font-size: 14px; padding: 12px 16px">Benchmark partenaires</v-card-title>
          <v-card-text style="padding: 0 16px 16px">
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Partenaire</th>
                    <th style="text-align: right">BDC</th>
                    <th style="text-align: right">CA HT</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in benchmarkDisplay" :key="p.partnerCode">
                    <td>{{ p.label }}</td>
                    <td style="text-align: right; font-weight: bold">{{ p.count }}</td>
                    <td style="text-align: right">{{ formatMoney(p.ht) }} €</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div v-if="!overview && !error" style="text-align: center; padding: 40px; color: #999">
      Chargement des statistiques...
    </div>
    <div v-if="error" style="text-align: center; padding: 40px; color: #f44336">
      <v-icon color="red" large>mdi-alert-circle-outline</v-icon>
      <div style="margin-top: 8px">Erreur lors du chargement des statistiques</div>
      <div style="font-size: 12px; color: #999; margin-top: 4px">{{ error }}</div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import server from "@/store/server";
import GaugeSegurCA from "@/components/Segur/GaugeSegurCA.vue";

const API_BASE = server.url;

const DEPT_NAMES = {
  "01": "Ain", "02": "Aisne", "03": "Allier", "04": "Alpes-de-Hte-Prov.", "05": "Hautes-Alpes",
  "06": "Alpes-Maritimes", "07": "Ardèche", "08": "Ardennes", "09": "Ariège", "10": "Aube",
  "11": "Aude", "12": "Aveyron", "13": "Bouches-du-Rhône", "14": "Calvados", "15": "Cantal",
  "16": "Charente", "17": "Charente-Maritime", "18": "Cher", "19": "Corrèze", "20": "Corse",
  "21": "Côte-d'Or", "22": "Côtes-d'Armor", "23": "Creuse", "24": "Dordogne", "25": "Doubs",
  "26": "Drôme", "27": "Eure", "28": "Eure-et-Loir", "29": "Finistère", "30": "Gard",
  "31": "Haute-Garonne", "32": "Gers", "33": "Gironde", "34": "Hérault", "35": "Ille-et-Vilaine",
  "36": "Indre", "37": "Indre-et-Loire", "38": "Isère", "39": "Jura", "40": "Landes",
  "41": "Loir-et-Cher", "42": "Loire", "43": "Haute-Loire", "44": "Loire-Atlantique", "45": "Loiret",
  "46": "Lot", "47": "Lot-et-Garonne", "48": "Lozère", "49": "Maine-et-Loire", "50": "Manche",
  "51": "Marne", "52": "Haute-Marne", "53": "Mayenne", "54": "Meurthe-et-Moselle", "55": "Meuse",
  "56": "Morbihan", "57": "Moselle", "58": "Nièvre", "59": "Nord", "60": "Oise",
  "61": "Orne", "62": "Pas-de-Calais", "63": "Puy-de-Dôme", "64": "Pyrénées-Atlantiques", "65": "Hautes-Pyrénées",
  "66": "Pyrénées-Orientales", "67": "Bas-Rhin", "68": "Haut-Rhin", "69": "Rhône", "70": "Haute-Saône",
  "71": "Saône-et-Loire", "72": "Sarthe", "73": "Savoie", "74": "Haute-Savoie", "75": "Paris",
  "76": "Seine-Maritime", "77": "Seine-et-Marne", "78": "Yvelines", "79": "Deux-Sèvres", "80": "Somme",
  "81": "Tarn", "82": "Tarn-et-Garonne", "83": "Var", "84": "Vaucluse", "85": "Vendée",
  "86": "Vienne", "87": "Haute-Vienne", "88": "Vosges", "89": "Yonne", "90": "Belfort",
  "91": "Essonne", "92": "Hauts-de-Seine", "93": "Seine-Saint-Denis", "94": "Val-de-Marne", "95": "Val-d'Oise",
  "971": "Guadeloupe", "972": "Martinique", "973": "Guyane", "974": "Réunion", "976": "Mayotte",
};


export default {
  name: "SegurStats",
  components: { GaugeSegurCA },
  data() {
    return {
      overview: null,
      error: null,
      showAlertes: false,
      exporting: false,
      exportingPdf: false,
    };
  },
  computed: {
    kpis() {
      return this.overview?.kpis;
    },

    psAtRiskDisplay() {
      return this.overview?.psAtRisk || [];
    },

    benchmarkDisplay() {
      return this.overview?.benchmark || [];
    },

    kpiCards() {
      if (!this.kpis) return [];
      return [
        {
          label: "BDC V2 total",
          value: this.kpis.bdcTotal,
          icon: "mdi-file-document-outline",
          color: "#e2007a",
          sub: `+${this.kpis.bdcThisMonth} ce mois`,
          subColor: this.kpis.bdcThisMonth > 0 ? "green" : "#888",
        },
        {
          label: "En cours (non soumis)",
          value: this.kpis.bdcEnable,
          icon: "mdi-clock-outline",
          color: "#ff9800",
          sub: this.kpis.pipeline30j > 0 ? `${this.kpis.pipeline30j} depuis > 30j` : null,
          subColor: "red",
        },
        {
          label: "Terminés (VA)",
          value: this.kpis.bdcOn,
          icon: "mdi-check-circle-outline",
          color: "#4caf50",
        },
        {
          label: "Taux de conversion",
          value: this.kpis.tauxConversion + "%",
          icon: "mdi-percent-outline",
          color: "#2196f3",
          sub: "BDC terminés / total",
        },
      ];
    },

    financialCardsV2() {
      if (!this.kpis) return [];
      const cf = this.overview?.cashFlow || {};
      return [
        { label: 'CA engagé', ht: this.kpis.totalHT, ttc: this.kpis.totalTTC, sub: this.kpis.bdcTotal + ' BDC' },
        { label: 'Avance reçue (40%)', ht: cf.avanceHT || 0, ttc: cf.avanceTTC || 0, sub: 'Soumis ASP' },
        { label: 'Solde facturé (60%)', ht: cf.soldeHT || 0, ttc: cf.soldeTTC || 0, sub: 'Facturé' },
        { label: 'Reste à encaisser', ht: cf.enAttenteHT || 0, ttc: cf.enAttenteTTC || 0, sub: 'Non encore reçu' },
      ];
    },

    hasBenchmark() {
      return this.benchmarkDisplay.length > 1;
    },

    // Alertes
    alerteType() {
      if (!this.overview?.alertes) return 'success';
      if (this.overview.alertes.critical > 0) return 'error';
      if (this.overview.alertes.warning > 0) return 'warning';
      return 'success';
    },
    alerteIcon() {
      if (!this.overview?.alertes || this.overview.alertes.total === 0) return 'mdi-check-circle';
      if (this.overview.alertes.critical > 0) return 'mdi-alert-circle';
      return 'mdi-alert';
    },

    // Mix V2/V1V12
    panierMoyen() {
      if (!this.kpis || this.kpis.bdcTotal === 0) return null;
      return (this.kpis.totalHT / this.kpis.bdcTotal).toFixed(2);
    },
    mixOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Roboto, sans-serif', stacked: true },
        xaxis: {
          categories: (this.overview?.mixParMois || []).map(e => e.mois),
          labels: { style: { fontSize: '11px' } },
        },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        colors: ['#e2007a', '#ff9800'],
        plotOptions: { bar: { borderRadius: 3, columnWidth: '60%' } },
        dataLabels: { enabled: false },
        legend: { position: 'top', fontSize: '12px' },
        tooltip: {
          theme: 'light', shared: true, intersect: false,
          y: { formatter: (val) => val + ' BDC' },
        },
      };
    },
    mixSeries() {
      const mix = this.overview?.mixParMois || [];
      return [
        { name: 'V2 (déjà équipé V1)', data: mix.map(e => e.V2) },
        { name: 'V1V12 (nouvel équipement)', data: mix.map(e => e.V1V12) },
      ];
    },

    // Rejets ASP
    rejetsColor() {
      if (!this.overview?.rejetsAsp) return 'green';
      const t = this.overview.rejetsAsp.tauxRejet;
      if (t > 15) return 'red';
      if (t > 5) return 'orange';
      return this.overview.rejetsAsp.total > 0 ? 'orange' : 'green';
    },
    rejetsDonutOptions() {
      const motifs = this.overview?.rejetsAsp?.parMotif || [];
      return {
        chart: { fontFamily: 'Roboto, sans-serif' },
        labels: motifs.map(m => m.libelle || m.code),
        colors: ['#f44336', '#ff5722', '#ff9800', '#ffc107', '#9e9e9e'],
        legend: { position: 'bottom', fontSize: '11px' },
        dataLabels: { enabled: true, formatter: (val) => Math.round(val) + '%' },
      };
    },
    rejetsDonutSeries() {
      return (this.overview?.rejetsAsp?.parMotif || []).map(m => m.count);
    },

    // BDC zombies
    bdcZombiesDisplay() {
      return this.overview?.bdcZombies || [];
    },

    evolutionOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
        xaxis: {
          categories: (this.overview?.evolutionV2 || []).map((e) => e.mois),
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        colors: ["#e2007a"],
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
        dataLabels: { enabled: false },
        tooltip: { theme: "light" },
      };
    },

    evolutionSeries() {
      return [{ name: "BDC V2", data: (this.overview?.evolutionV2 || []).map((e) => e.count) }];
    },

    donutOptions() {
      return {
        chart: { fontFamily: "Roboto, sans-serif" },
        labels: ["Non signé", "Signé", "Terminé", "Désactivé"],
        colors: ["#f44336", "#ff9800", "#4caf50", "#9e9e9e"],
        legend: { position: "bottom", fontSize: "12px" },
        dataLabels: { enabled: true, formatter: (val) => Math.round(val) + "%" },
      };
    },

    donutSeries() {
      const s = this.overview?.stateCount || {};
      return [s.OFF || 0, s.ENABLE || 0, s.ON || 0, s.DISABLE || 0];
    },

    caOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
        xaxis: {
          categories: (this.overview?.caParMois || []).map((e) => e.mois),
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            style: { fontSize: "11px" },
            formatter: (val) => val.toLocaleString("fr-FR") + " €",
          },
        },
        colors: ["#e2007a"],
        plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        tooltip: {
          theme: "light",
          y: { formatter: (val) => val.toLocaleString("fr-FR") + " € HT" },
        },
      };
    },

    caSeries() {
      return [{ name: "CA HT", data: (this.overview?.caParMois || []).map((e) => e.ht) }];
    },

    deptOptions() {
      const depts = this.overview?.topDepartements || [];
      return {
        chart: { toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
        plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "65%" } },
        colors: ["#e2007a"],
        xaxis: {
          categories: depts.map((e) => DEPT_NAMES[e.dept] ? `${DEPT_NAMES[e.dept]} (${e.dept})` : e.dept),
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: { labels: { style: { fontSize: "12px" } } },
        dataLabels: { enabled: true, style: { fontSize: "11px" }, offsetX: 4 },
        grid: { xaxis: { lines: { show: false } } },
        tooltip: { theme: "light", y: { formatter: (val) => val + " BDC" } },
      };
    },

    deptSeries() {
      return [{ name: "BDC", data: (this.overview?.topDepartements || []).map((e) => e.count) }];
    },

    comparisonOptions() {
      const maxLen = Math.max(
        (this.overview?.cumulatifV2 || []).length,
        (this.overview?.cumulatifV1 || []).length
      );
      const labels = Array.from({ length: maxLen }, (_, i) => `M+${i}`);
      return {
        chart: { toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
        xaxis: { categories: labels, labels: { style: { fontSize: "11px" } } },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        colors: ["#e2007a", "#9e9e9e"],
        stroke: { width: [3, 2], dashArray: [0, 5] },
        dataLabels: { enabled: false },
        tooltip: { theme: "light", shared: true, intersect: false },
        legend: { position: "top" },
      };
    },

    comparisonSeries() {
      return [
        { name: "V2 (cumulé depuis lancement)", data: (this.overview?.cumulatifV2 || []).map((e) => e.cumul) },
        { name: "V1 (cumulé depuis lancement)", data: (this.overview?.cumulatifV1 || []).map((e) => e.cumul) },
      ];
    },
  },
  async created() {
    await this.fetchOverview();
  },
  methods: {
    async fetchOverview() {
      try {
        this.error = null;
        const res = await axios.get(`${API_BASE}/segur/v2/stats/overview`, {
          headers: server.getHeader(),
        });
        if (res.data && res.data.success) {
          this.overview = res.data.data;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[SegurStats] Erreur:", err.message);
        this.error = err.message;
      }
    },
    async exportXlsx() {
      try {
        this.exporting = true;
        const res = await axios.get(`${API_BASE}/segur/v2/stats/export`, {
          headers: server.getHeader(),
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        const date = new Date().toISOString().slice(0, 10);
        link.setAttribute("download", `SEGUR-V2-${date}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[SegurStats] Export erreur:", err.message);
      } finally {
        this.exporting = false;
      }
    },
    async exportPdf() {
      try {
        this.exportingPdf = true;
        const res = await axios.get(`${API_BASE}/segur/v2/stats/export-pdf`, {
          headers: server.getHeader(),
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        const date = new Date().toISOString().slice(0, 10);
        link.setAttribute("download", `SEGUR-V2-${date}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[SegurStats] Export PDF erreur:", err.message);
      } finally {
        this.exportingPdf = false;
      }
    },
    stepBackground(index) {
      const colors = ['#e2007a', '#d4006e', '#b8005e', '#9a004f'];
      return colors[index] || colors[colors.length - 1];
    },
    formatAvgDays(days) {
      if (days == null) return "-";
      return days < 1 ? "< 1j" : Math.round(days) + "j";
    },
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("fr-FR");
    },
    formatMoney(val) {
      return (val || 0).toLocaleString("fr-FR");
    },
  },
};
</script>

<style scoped>
.content {
  max-width: 100%;
}
.alert-card::before {
  display: none !important;
}
.export-btn {
  background: white;
  flex-shrink: 0;
  margin-left: auto;
  border-radius: 8px;
  height: 44px !important;
  text-transform: none;
  letter-spacing: 0;
  font-size: 13px;
}
.export-btn:hover {
  background: #fce4ec !important;
}
</style>
